/**
 * Play / pause button + matchday scrubber for the standings chart timeline.
 */
import {
  HStack,
  IconButton,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { FaPause, FaPlay } from 'react-icons/fa';

import type { ChartPlayback } from './useChartPlayback';

export interface PlaybackControlsProps {
  playback: ChartPlayback;
  maxMatchday: number;
}

export function PlaybackControls({ playback, maxMatchday }: PlaybackControlsProps) {
  const currentMatchday = Math.round(playback.visibleThrough);
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const thumbSize = isMobile ? 5 : 4;

  const playButton = (
    <IconButton
      aria-label={playback.playing ? 'Pause' : 'Play'}
      icon={playback.playing ? <FaPause /> : <FaPlay />}
      size='sm'
      colorScheme='green'
      borderRadius='full'
      // PointerDown fires before click and survives main-thread contention from
      // chart re-renders during playback (mobile pause was often dropped).
      onPointerDown={(e) => {
        if (maxMatchday < 1) return;
        e.preventDefault();
        if (playback.playing) playback.pause();
        else playback.play();
      }}
      isDisabled={maxMatchday < 1}
    />
  );

  const slider = (
    <Slider
      aria-label='Matchday timeline'
      min={0}
      max={Math.max(1, maxMatchday)}
      step={0.25}
      value={playback.visibleThrough}
      onChange={playback.seek}
      focusThumbOnChange={false}
      flex='1'
      w={isMobile ? 'full' : undefined}
      isDisabled={maxMatchday < 1}
    >
      <SliderTrack>
        <SliderFilledTrack bg='brandAccent' />
      </SliderTrack>
      <SliderThumb boxSize={thumbSize} />
    </Slider>
  );

  const label = (
    <Text fontSize='xs' color='gray.500' minW={isMobile ? undefined : '84px'} textAlign='right'>
      MatchDay {currentMatchday} / {maxMatchday}
    </Text>
  );

  if (isMobile) {
    return (
      <VStack spacing={2} w='full' align='stretch'>
        {slider}
        <HStack spacing={3} justify='space-between'>
          {playButton}
          {label}
        </HStack>
      </VStack>
    );
  }

  return (
    <HStack spacing={3} w='full'>
      {playButton}
      {slider}
      {label}
    </HStack>
  );
}
